import React, { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Import Stats from External Link Component
 * Allows users to import their match history from external platforms
 */
const ImportStatsFromLink = ({ token, theme, onSuccess }) => {
  const [step, setStep] = useState('input'); // input, analyzing, preview, importing, success
  const [url, setUrl] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [error, setError] = useState(null);

  const isDark = theme === 'dark';

  // API helper
  const api = useCallback(async (endpoint, options = {}) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    });
    
    return response;
  }, [token]);

  // Analyze URL
  const handleAnalyze = async () => {
    if (!url.trim()) {
      setError('Inserisci un link valido');
      return;
    }

    setError(null);
    setStep('analyzing');

    try {
      const response = await api('/api/import-stats/analyze', {
        method: 'POST',
        body: JSON.stringify({ url: url.trim() })
      });

      const data = await response.json();

      if (data.success) {
        setAnalysisResult(data);
        setStep('preview');
      } else {
        setError(data.error || 'Errore durante l\'analisi');
        setStep('input');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Errore di connessione');
      setStep('input');
    }
  };

  // Execute import
  const handleImport = async () => {
    setStep('importing');
    setError(null);

    try {
      const response = await api('/api/import-stats/execute', {
        method: 'POST',
        body: JSON.stringify({
          url: url.trim(),
          matches: analysisResult.matches,
          replace_existing: replaceExisting
        })
      });

      const data = await response.json();

      if (data.success) {
        setStep('success');
        if (onSuccess) {
          setTimeout(() => onSuccess(data), 2000);
        }
      } else {
        setError(data.error || 'Errore durante l\'importazione');
        setStep('preview');
      }
    } catch (err) {
      console.error('Import error:', err);
      setError('Errore di connessione');
      setStep('preview');
    }
  };

  // Reset
  const handleReset = () => {
    setStep('input');
    setUrl('');
    setAnalysisResult(null);
    setReplaceExisting(false);
    setError(null);
  };

  return (
    <div className={`p-6 rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <span className="mr-2">🔗</span> Importa Risultati da Link
      </h3>
      
      <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
        Importa le tue statistiche e partite da piattaforme esterne incollando il link del tuo profilo.
      </p>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Step: Input */}
      {step === 'input' && (
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Link Statistiche Giocatore
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300'
              }`}
            />
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Incolla il link completo della pagina con le tue statistiche
            </p>
          </div>
          <button
            onClick={handleAnalyze}
            disabled={!url.trim()}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            🔍 Analizza Link
          </button>
        </div>
      )}

      {/* Step: Analyzing */}
      {step === 'analyzing' && (
        <div className="text-center py-8">
          <div className="animate-spin h-12 w-12 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"></div>
          <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Analisi in corso...</p>
          <p className={`text-sm mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Estrazione dati dalla pagina
          </p>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && analysisResult && (
        <div className="space-y-4">
          {/* Player Info */}
          {analysisResult.player_name && (
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <p className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                👤 Giocatore: {analysisResult.player_name}
              </p>
            </div>
          )}

          {/* Statistics Preview */}
          <div className="grid grid-cols-3 gap-3">
            <div className={`p-3 rounded-lg text-center ${isDark ? 'bg-green-900/30' : 'bg-green-50'}`}>
              <div className={`text-xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                {analysisResult.import_preview?.vittorie || 0}
              </div>
              <div className={`text-xs ${isDark ? 'text-green-300' : 'text-green-700'}`}>Vittorie</div>
            </div>
            <div className={`p-3 rounded-lg text-center ${isDark ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
              <div className={`text-xl font-bold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                {analysisResult.import_preview?.pareggi || 0}
              </div>
              <div className={`text-xs ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>Pareggi</div>
            </div>
            <div className={`p-3 rounded-lg text-center ${isDark ? 'bg-red-900/30' : 'bg-red-50'}`}>
              <div className={`text-xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                {analysisResult.import_preview?.sconfitte || 0}
              </div>
              <div className={`text-xs ${isDark ? 'text-red-300' : 'text-red-700'}`}>Sconfitte</div>
            </div>
          </div>

          {/* Goals */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`p-3 rounded-lg text-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                ⚽ {analysisResult.import_preview?.gol_fatti || 0}
              </div>
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Gol Fatti</div>
            </div>
            <div className={`p-3 rounded-lg text-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                🥅 {analysisResult.import_preview?.gol_subiti || 0}
              </div>
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Gol Subiti</div>
            </div>
          </div>

          {/* Matches Count */}
          <div className={`p-4 rounded-lg border ${isDark ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex justify-between items-center">
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Partite trovate:</span>
              <span className="text-xl font-bold text-blue-600">{analysisResult.total_matches_found || 0}</span>
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="replace-existing"
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded"
            />
            <label htmlFor="replace-existing" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Sostituisci partite importate precedentemente
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleImport}
              disabled={!analysisResult.total_matches_found}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              ✅ Importa {analysisResult.total_matches_found} Partite
            </button>
            <button
              onClick={handleReset}
              className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                isDark 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ❌
            </button>
          </div>
        </div>
      )}

      {/* Step: Importing */}
      {step === 'importing' && (
        <div className="text-center py-8">
          <div className="animate-spin h-12 w-12 border-4 border-green-200 border-t-green-600 rounded-full mx-auto mb-4"></div>
          <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Importazione in corso...</p>
          <p className={`text-sm mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Salvataggio di {analysisResult?.total_matches_found || 0} partite
          </p>
        </div>
      )}

      {/* Step: Success */}
      {step === 'success' && (
        <div className="text-center py-8">
          <div className="text-5xl mb-4">🎉</div>
          <h4 className={`text-xl font-bold mb-2 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            Importazione Completata!
          </h4>
          <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
            Le tue partite sono state importate con successo
          </p>
          <button
            onClick={handleReset}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            🔗 Importa altro
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportStatsFromLink;
